/**
 * Générateur — Savoir écrire un nombre sous la forme d'une puissance 2 ou 3.
 * Attendu ID : 929 | Niveau : 5ème | Domaine : Puissances (cluster 2, O31)
 *
 * Plan de cartes (validé 2026-07-11) :
 *
 *   Carte « écrire » — Ex.9 Écrire en puissance (⚠️ flag correcteur du doc)
 *     ADAPTATION (celle que le doc demande) : l'exposant est VERROUILLÉ dans
 *     la phrase, l'élève ne remplit que la base : « 27 = {{3}}³ » — réponse
 *     unique par construction (16 = {{4}}² : l'écriture 2⁴ est inatteignable,
 *     l'exposant ² est imposé). texte_trou_saisie + qcm_unique (« Quelle
 *     écriture est égale à v ? ») ; les nombres à DOUBLE écriture (1, 64)
 *     sont exclus du QCM (4³ ET 8² y seraient justes).
 *
 *   Carte « deux écritures » — Créé n°21 (reprend le 64 = 8² = 4³ du cadrage)
 *     Le « menu déroulant par case » du doc devient l'étiquette « impossible ».
 *     texte_trou_drag : 64 (l'exemple du cadrage) + un nombre à écriture
 *     unique — EXACTEMENT un « impossible » par tirage et 16 exclu (base 4
 *     dupliquerait l'étiquette du 4³ de 64 : une étiquette ne peut servir
 *     deux trous). texte_trou_saisie : combinaisons libres (l'élève écrit
 *     « impossible » en toutes lettres), y compris 1 = 1² = 1³ et le couple
 *     carré-seul/cube-seul à deux « impossible ».
 *
 *   Carte « classer » — Créé n°22 Carré, cube, ou ni l'un ni l'autre ?
 *     classer (4 catégories) + tableau colonnesLibres. Libellés EXCLUSIFS
 *     (« Carré seulement », « Carré ET cube »…) : sans « seulement », 64
 *     serait classable dans deux colonnes. Pools disjoints par construction.
 *
 *   Carte « V/F » — Créé n°23 V/F écritures
 *     tableau_vf : vraies = égalités de table ; fausses par familles —
 *     échange carré/cube (100 = 10³), base voisine (144 = 11²), piège
 *     « racine de racine » du doc (27 = 9²). 5 lignes, 2-3 vraies.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeClasser, filtrerDistracteurs,
    basePayload,
} from '../../../utils';

const ATTENDU_ID = 929;

const L_ECRIRE = 'Écrire en puissance';
const L_DEUX = 'Deux écritures possibles';
const L_CLASSER = 'Carré, cube, ou ni l\'un ni l\'autre ?';
const L_VF = 'Vrai ou faux : les écritures';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const SUP = { 2: '²', 3: '³' };
const puiss = (a, n) => `${a}${SUP[n]}`;

// ═════════ Pools structurels (disjoints par construction) ═════════

// Carrés de la table 2..12 qui ne sont PAS des cubes (64 exclu)
const CARRES_SEULS = [4, 9, 16, 25, 36, 49, 81, 100, 121, 144];
// Cubes 2..6 et 10 qui ne sont PAS des carrés de la table (64 exclu)
const CUBES_SEULS = [8, 27, 125, 216, 1000];
// Carré ET cube à la fois
const LES_DEUX = [1, 64];
// Ni carré ni cube (vérifié : aucun carré ≤ 40², aucun cube ≤ 10³)
const NI_LUN_NI_LAUTRE = [2, 3, 5, 6, 7, 10, 12, 15, 20, 24, 30, 40, 50, 90, 110, 200];

const racineCarree = (v) => { const r = Math.round(Math.sqrt(v)); return r * r === v ? r : null; };
const racineCubique = (v) => { const r = Math.round(Math.cbrt(v)); return r ** 3 === v ? r : null; };

// ═════════ Carte 1 — Écrire en puissance (Ex.9) ═════════

const genEcrireTrous = () => {
    // 3 items : 2 carrés + 1 cube, valeurs deux à deux distinctes
    const [a1, a2] = shuffle([2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]).slice(0, 2);
    const b = randomChoice([2, 3, 4, 5, 6]);
    const items = shuffle([
        `${a1 * a1} = {{${a1}}}²`,
        `${a2 * a2} = {{${a2}}}²`,
        `${b ** 3} = {{${b}}}³`,
    ]);
    // Une égalité par ligne (convention 2026-09-02 : jamais « ; » entre deux
    // égalités à trous, l'impression coupait la ligne au milieu).
    const phrase = items.join('\n');
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète chaque écriture avec le bon nombre entier.</b> '
        + '(L\'exposant est déjà donné : trouve la base.)</p>',
        { ...opts(L_ECRIRE), mode: 'saisie' });
    payload._meta.params = { a1, a2, b };
    return payload;
};

const genEcrireQcm = () => {
    const type = randomChoice(['carre', 'cube']);
    let v, correct, candidats, params;
    if (type === 'carre') {
        // a = 8 exclu : 64 a DEUX écritures (8² et 4³), le QCM serait ambigu
        const a = randomChoice([2, 3, 4, 5, 6, 7, 9, 10, 11, 12]);
        v = a * a;
        correct = puiss(a, 2);
        candidats = [
            puiss(a, 3),                       // échange carré/cube (valeur a³ ≠ v)
            puiss(a + 1, 2),                   // base voisine
            puiss(a - 1, 2),                   // base voisine
            a !== 2 ? `${a} × 2` : null,       // a × n (a = 2 exclu : 2 × 2 = 4 serait juste)
        ].filter(Boolean);
        params = { type, a };
    } else {
        // b = 4 exclu : 64 (double écriture)
        const b = randomChoice([2, 3, 5, 6]);
        v = b ** 3;
        correct = puiss(b, 3);
        candidats = [
            puiss(b, 2),                       // échange cube/carré
            puiss(b + 1, 3),                   // base voisine
            puiss(b - 1, 3),                   // base voisine
            `${b} × 3`,                        // b × n (b × 3 = b³ n'a pas de solution entière ≥ 2)
        ];
        params = { type, b };
    }
    const distracteurs = shuffle([...new Set(candidats)].filter(c => c !== correct)).slice(0, 3);
    const payload = makeQcm([correct, ...distracteurs], correct,
        `<p><b>Quelle écriture est égale à ${v} ?</b></p>`, opts(L_ECRIRE));
    payload._meta.params = params;
    return payload;
};

// ═════════ Carte 2 — Deux écritures possibles (Créé n°21) ═════════

// segment « v = {{base ou impossible}}² = {{base ou impossible}}³ »
const segmentDeux = (v) => {
    const r2 = racineCarree(v);
    const r3 = racineCubique(v);
    return `${v} = {{${r2 ?? 'impossible'}}}² = {{${r3 ?? 'impossible'}}}³`;
};

const genDeuxDrag = () => {
    // 64 (l'exemple du cadrage, bases 8 et 4) + un nombre à écriture unique.
    // 16 exclu (base 4 = étiquette déjà prise par le cube de 64) → exactement
    // un « impossible » et des étiquettes toutes distinctes.
    const autre = randomChoice([...CARRES_SEULS.filter(v => v !== 16), ...CUBES_SEULS]);
    const segments = shuffle([segmentDeux(64), segmentDeux(autre)]);
    const phrase = segments.join('\n'); // une égalité par ligne
    const baseAutre = racineCarree(autre) ?? racineCubique(autre);
    const distracteurs = filtrerDistracteurs(phrase, [
        String(baseAutre + 1),      // voisin de la base
        String(baseAutre - 1),
        '32',                       // la moitié de 64
        '6',                        // voisin des bases de 64
        '16',                       // le « quart » de 64
    ]).slice(0, 3);
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète chaque écriture.</b> Si c\'est impossible, glisse l\'étiquette '
        + '« impossible ». (Rappel du cours : 64 = 8² = 4³.)</p>',
        { ...opts(L_DEUX), mode: 'drag', distracteurs });
    payload._meta.params = { autre };
    return payload;
};

const genDeuxSaisie = () => {
    // combinaisons libres : les deux « impossible » sont permis (l'élève écrit le mot)
    const pool = [...LES_DEUX, ...CARRES_SEULS, ...CUBES_SEULS];
    const [v1, v2] = shuffle(pool).slice(0, 2);
    const phrase = `${segmentDeux(v1)}\n${segmentDeux(v2)}`; // une égalité par ligne
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète chaque écriture avec un nombre entier.</b> '
        + 'Si c\'est impossible, écris le mot « impossible ».</p>',
        { ...opts(L_DEUX), mode: 'saisie' });
    payload._meta.params = { v1, v2 };
    return payload;
};

// ═════════ Carte 3 — Carré, cube, ou ni l'un ni l'autre ? (Créé n°22) ═════════

const CAT_CARRE = 'Carré seulement';
const CAT_CUBE = 'Cube seulement';
const CAT_DEUX = 'Carré ET cube';
const CAT_NI = 'Ni carré ni cube';

const tirerClassement = () => ({
    carres: shuffle(CARRES_SEULS).slice(0, 2).map(String),
    cubes: shuffle(CUBES_SEULS).slice(0, 2).map(String),
    deux: [String(randomChoice(LES_DEUX))],
    ni: shuffle(NI_LUN_NI_LAUTRE).slice(0, 2).map(String),
});

const genClasserClasser = () => {
    const t = tirerClassement();
    const payload = makeClasser([
        { name: CAT_CARRE, items: t.carres },
        { name: CAT_CUBE, items: t.cubes },
        { name: CAT_DEUX, items: t.deux },
        { name: CAT_NI, items: t.ni },
    ],
    '<p><b>Classe chaque nombre dans la bonne catégorie.</b> '
    + '(« Carré » signifie : carré d\'un nombre entier.)</p>',
    opts(L_CLASSER));
    payload._meta.params = t;
    return payload;
};

const genClasserTableau = () => {
    const t = tirerClassement();
    const colonnes = [CAT_CARRE, CAT_CUBE, CAT_DEUX, CAT_NI];
    const entrees = shuffle([
        ...t.carres.map(v => ({ v, col: 0 })),
        ...t.cubes.map(v => ({ v, col: 1 })),
        ...t.deux.map(v => ({ v, col: 2 })),
        ...t.ni.map(v => ({ v, col: 3 })),
    ]).slice(0, 5);
    const lignes = entrees.map(e => e.v);
    const bonnesReponses = entrees.map(e => e.col);
    const br = lignes.map((l, i) => `[${l}|${colonnes[bonnesReponses[i]]}]`).join('');
    const tableElement = {
        type: 'table',
        config: { type: 'colonnesLibres', colonnes, lignes, bonnesReponses },
    };
    const payload = basePayload('qcm',
        '<p><b>Pour chaque nombre, coche la bonne colonne.</b> '
        + '(« Carré » signifie : carré d\'un nombre entier.)</p>',
        { choix: [], bonne_reponse: br },
        opts(L_CLASSER),
        { version: 1, elements: [tableElement] });
    payload._meta.params = { lignes, bonnesReponses };
    return payload;
};

// ═════════ Carte 4 — V/F écritures (Créé n°23) ═════════

const genVfEcritures = () => {
    const lignes = [];
    const bonnes = [];
    const vues = new Set();
    const ajouter = (ligne, estVraie) => {
        if (vues.has(ligne)) return false;
        vues.add(ligne);
        lignes.push(ligne);
        bonnes.push(estVraie ? 0 : 1);
        return true;
    };
    const nbVrai = randInt(2, 3);
    // vraies : égalités de table
    for (let k = 0; k < nbVrai; k++) {
        for (let essai = 0; essai < 20; essai++) {
            if (randomChoice([true, false])) {
                const a = randInt(2, 12);
                if (ajouter(`${a * a} = ${puiss(a, 2)}`, true)) break;
            } else {
                const b = randomChoice([2, 3, 4, 5, 6, 10]);
                if (ajouter(`${b ** 3} = ${puiss(b, 3)}`, true)) break;
            }
        }
    }
    // fausses : familles de misconceptions (fausses par construction, cf. plan)
    const famillesFausses = shuffle(['echange', 'voisine', 'racineDeRacine', 'echange', 'voisine']);
    for (const famille of famillesFausses) {
        if (lignes.length >= 5) break;
        for (let essai = 0; essai < 20; essai++) {
            let ok = false;
            if (famille === 'echange') {
                // échange carré/cube — faux par construction (x² ≠ x³ pour x ≥ 2)
                if (randomChoice([true, false])) {
                    const a = randInt(2, 10);
                    ok = ajouter(`${a * a} = ${puiss(a, 3)}`, false);   // ex. 100 = 10³ (le doc)
                } else {
                    const b = randomChoice([2, 3, 4, 5, 6, 10]);
                    ok = ajouter(`${b ** 3} = ${puiss(b, 2)}`, false);  // ex. 1000 = 10²
                }
            } else if (famille === 'voisine') {
                const a = randInt(3, 12);
                ok = ajouter(`${a * a} = ${puiss(a - 1, 2)}`, false);
            } else {
                // le piège « racine de racine » du doc : 27 = 9² (9 = 3², b⁴ ≠ b³)
                const b = randomChoice([2, 3, 4, 5]);
                ok = ajouter(`${b ** 3} = ${puiss(b * b, 2)}`, false);
            }
            if (ok) break;
        }
    }
    const payload = makeVraiFaux(lignes, bonnes,
        '<p><b>Vrai ou faux ?</b> Pour chaque égalité, coche la bonne case.</p>',
        opts(L_VF));
    payload._meta.params = { lignes, bonnes };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    ecrire_texte_trou_saisie: genEcrireTrous,
    ecrire_qcm_unique: genEcrireQcm,
    deux_texte_trou_drag: genDeuxDrag,
    deux_texte_trou_saisie: genDeuxSaisie,
    classer_classer: genClasserClasser,
    classer_tableau_colonnes: genClasserTableau,
    vf_tableau_vf: genVfEcritures,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', texte_trou_drag: 'Drag', qcm_unique: 'QCM',
    classer: 'Classer', tableau_colonnes: 'Tableau', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'ecrire', label: L_ECRIRE, formatsList: buildFormats('ecrire') },
    { id: 'deux', label: L_DEUX, formatsList: buildFormats('deux') },
    { id: 'classer', label: L_CLASSER, formatsList: buildFormats('classer') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
